// ExpertQuotesMobileComponent.js
import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import Image from '../../Image/Image';

const INLINE_BOLD_REGEX = /(\*\*[^*]+\*\*|<b>[^<]+<\/b>)/g;
const EXTERNAL_URL_REGEX = /^https?:\/\//i;

const normalizeExternalUrl = (href) => {
  if (!href || typeof href !== 'string') {
    return undefined;
  }

  const trimmedHref = href.trim();
  if (!trimmedHref) {
    return undefined;
  }

  return EXTERNAL_URL_REGEX.test(trimmedHref)
    ? trimmedHref
    : `https://${trimmedHref}`;
};

const renderRichText = (text) =>
  String(text || '')
    .split(INLINE_BOLD_REGEX)
    .map((part, index) => {
      if (/^\*\*[^*]+\*\*$/.test(part)) {
        return (
          <strong key={index} style={{ fontWeight: 700 }}>
            {part.slice(2, -2)}
          </strong>
        );
      }

      if (/^<b>[^<]+<\/b>$/.test(part)) {
        return (
          <strong key={index} style={{ fontWeight: 700 }}>
            {part.slice(3, -4)}
          </strong>
        );
      }

      return part;
    });

const ExpertQuotesMobileComponent = ({ expertData }) => {
  const { image, quoteTitle, quoteText, expertName, expertLink } = expertData;
  const normalizedExpertLink = normalizeExternalUrl(expertLink);

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100vw',
        padding: '0 16px',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          width: '100%',
          backgroundColor: '#162F3B',
          borderRadius: '34px',
          padding: '32px 16px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '32px',
          opacity: 1,
        }}
      >
        {/* Expert Image */}
        <Image
          width={104}
          height={104}
          src={image}
          alt="Expert"
          sx={{
            width: '104px',
            height: '104px',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />

        {/* Quote Text */}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.5rem',
            lineHeight: '1.75rem',
            letterSpacing: '-0.03em',
            textAlign: 'left',
            color: '#FFFFFF',
          }}
        >
          {quoteTitle ? <strong>{quoteTitle}</strong> : null}{' '}
          {renderRichText(quoteText)}
        </Typography>

        {/* Expert Name */}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 700,
            fontSize: '1rem',
            lineHeight: '1.5rem',
            letterSpacing: '-0.02em',
            color: '#FFFFFF',
          }}
        >
          {expertName?.split(/(\*\*[^*]+\*\*|<G>[^<]+<G>)/g).map((part, i) => {
            if (/^\*\*[^*]+\*\*$/.test(part)) {
              // bold text between ** **
              return (
                <strong key={i} style={{ fontWeight: 700 }}>
                  {part.slice(2, -2)}
                </strong>
              );
            } else if (/^<G>[^<]+<G>$/.test(part)) {
              // green text between <G> <G>
              return (
                <span key={i} style={{ color: '#C7CA00' }}>
                  {
                    <Link
                      href={normalizedExpertLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="none"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        maxWidth: '100%',
                        width: 'fit-content',
                        px: '4px',
                        py: '2px',
                        borderRadius: '4px',
                        boxSizing: 'border-box',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        color: '#C7CA00',
                        fontWeight: 500,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline',
                          opacity: 0.9, // optional subtle feedback
                        },
                      }}
                    >
                      {part.slice(3, -3)}
                    </Link>
                  }
                </span>
              );
            } else {
              return part;
            }
          })}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExpertQuotesMobileComponent;
